"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";

// ----- localStorage recents (from old HomeQuickRoom) -----
const RECENT_KEY = "jobutu.recent.rooms";
const MAX_RECENTS = 8;

interface RecentRoom {
  name: string;
  lastVisitedMs: number;
  fav: boolean;
  displayName?: string;
  customImage?: string;
  autoAvatar?: string;
}

function readRecents(): RecentRoom[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as RecentRoom[];
    if (!Array.isArray(arr)) return [];
    return arr.filter(
      (r) => r && typeof r.name === "string" && typeof r.lastVisitedMs === "number",
    );
  } catch {
    return [];
  }
}

function writeRecents(list: RecentRoom[]): void {
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, MAX_RECENTS)));
  } catch { /* noop */ }
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 5) return "Up late";
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function relTime(ms: number): string {
  const diff = Date.now() - ms;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function timeLeft(ms: number): string {
  const diff = ms - Date.now();
  if (diff < 0) return "now";
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `in ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `in ${h}h`;
  const d = Math.floor(h / 24);
  return `in ${d}d`;
}

const SLUG_RE = /^[A-Za-z0-9_-]{1,64}$/;

interface CalendarEvent {
  id: string;
  summary: string;
  description: string | null;
  startMs: number;
  endMs: number;
  htmlLink: string;
  location: string | null;
  joinUrl: string | null;
  attendees: { email: string; responseStatus?: string }[];
}

interface Schedule {
  id: number;
  roomName: string;
  title: string;
  startMs: number;
  endMs: number;
  attendees: string[];
}

interface Props {
  signedIn: boolean;
  firstName: string | null;
  fullName: string | null;
  email: string | null;
  image: string | null;
}


function useCollapsed(key: string, initial = false): [boolean, () => void] {
  const [v, setV] = React.useState(initial);
  React.useEffect(() => {
    try {
      const raw = window.localStorage.getItem(`jobutu.collapse.${key}`);
      if (raw !== null) setV(raw === "1");
    } catch { /* noop */ }
  }, [key]);
  const toggle = React.useCallback(() => {
    setV((p) => {
      const n = !p;
      try { window.localStorage.setItem(`jobutu.collapse.${key}`, n ? "1" : "0"); } catch { /* noop */ }
      return n;
    });
  }, [key]);
  return [v, toggle];
}

function CollapsibleHead(props: {
  label: string;
  collapsed: boolean;
  onToggle: () => void;
  right?: React.ReactNode;
}) {
  return (
    <header className="block-head" style={{ cursor: "pointer", userSelect: "none" }}
      onClick={(e) => { if (e.target === e.currentTarget || (e.target as HTMLElement).tagName === "SPAN") props.onToggle(); }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }} onClick={props.onToggle}>
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 10, height: 10,
            transform: `rotate(${props.collapsed ? -90 : 0}deg)`,
            transition: "transform 200ms cubic-bezier(.2,.8,.2,1)",
            color: "currentColor",
          }}
        >
          <svg width="10" height="10" viewBox="0 0 12 12">
            <path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        {props.label}
      </span>
      {props.right ?? null}
    </header>
  );
}

export function HomeDashboard(props: Props) {
  const router = useRouter();
  const [recents, setRecents] = React.useState<RecentRoom[]>([]);
  const [editChip, setEditChip] = React.useState<RecentRoom | null>(null);
  const [upcomingCollapsed, toggleUpcoming] = useCollapsed("upcoming");
  const [calendarCollapsed, toggleCalendar] = useCollapsed("calendar");
  const [recentsCollapsed, toggleRecents] = useCollapsed("recents");
  const [schedules, setSchedules] = React.useState<Schedule[] | null>(null);
  const [calendarEvents, setCalendarEvents] = React.useState<CalendarEvent[] | null>(null);
  const [calendarReason, setCalendarReason] = React.useState<string | null>(null);
  const [expandedCal, setExpandedCal] = React.useState<Set<string>>(() => new Set());
  const [joinName, setJoinName] = React.useState("");
  const [openSchedule, setOpenSchedule] = React.useState(false);

  React.useEffect(() => {
    setRecents(readRecents());
  }, []);

  React.useEffect(() => {
    if (!props.signedIn) {
      setSchedules([]);
      setCalendarEvents([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/schedules");
        if (!res.ok) return;
        const j = (await res.json()) as { schedules: Schedule[] };
        if (!cancelled) setSchedules(j.schedules.slice(0, 4));
      } catch (e) { console.warn("schedules fetch", e); }
    })();
    return () => { cancelled = true; };
  }, [props.signedIn]);

  React.useEffect(() => {
    if (!props.signedIn) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/calendar/upcoming");
        if (!res.ok) return;
        const j = (await res.json()) as { events: CalendarEvent[] | null; reason?: string };
        if (cancelled) return;
        setCalendarEvents(j.events);
        setCalendarReason(j.reason ?? null);
      } catch (e) { console.warn("calendar fetch", e); }
    })();
    return () => { cancelled = true; };
  }, [props.signedIn]);

  const goRoom = React.useCallback(
    (name: string) => {
      if (!name || !SLUG_RE.test(name)) return;
      const list = readRecents();
      const existing = list.find((r) => r.name === name);
      const next = [
        { name, lastVisitedMs: Date.now(), fav: existing?.fav ?? false },
        ...list.filter((r) => r.name !== name),
      ];
      writeRecents(next);
      setRecents(next);
      router.push(`/rooms/${encodeURIComponent(name)}`);
    },
    [router],
  );

  const startFresh = React.useCallback(() => {
    const id = Math.random().toString(36).slice(2, 6) + "-" + Math.random().toString(36).slice(2, 6);
    goRoom(id);
  }, [goRoom]);

  const dedupedSchedules = React.useMemo(() => {
    if (!schedules) return null;
    // Drop schedules whose endMs is in the past — those are stale "Live
    // now" entries left over after the call ended. Keep a 2-minute grace
    // window so an actively-running call doesn't blink off due to clock
    // skew between client and server.
    const cutoff = Date.now() - 2 * 60_000;
    const fresh = schedules.filter((s) => s.endMs > cutoff);
    const map = new Map<string, Schedule>();
    for (const s of fresh) {
      const cur = map.get(s.roomName);
      if (!cur || cur.startMs < s.startMs) map.set(s.roomName, s);
    }
    return Array.from(map.values()).sort((a, b) => a.startMs - b.startMs);
  }, [schedules]);

  const sortedRecents = React.useMemo(() => {
    return [...recents].sort((a, b) => {
      if (a.fav !== b.fav) return a.fav ? -1 : 1;
      return b.lastVisitedMs - a.lastVisitedMs;
    });
  }, [recents]);

  return (
    <div className="dash">
      {/* Hero */}
      <section className="hero">
        <div className="hero-id">
          {props.image ? (
            <img src={props.image} alt="" width={64} height={64} className="hero-avatar" />
          ) : (
            <span className="hero-avatar fallback">
              {(props.firstName ?? "?").charAt(0).toUpperCase()}
            </span>
          )}
          <div className="hero-text">
            <h1 className="hero-greet">
              {greeting()}{props.firstName ? `, ${props.firstName}` : ""}
            </h1>
            {props.email ? <p className="hero-mail">{props.email}</p> : null}
          </div>
        </div>
        <p className="hero-sub">
          Hop into a room or schedule one for later. Your boards stay private to your account.
        </p>
      </section>

      {/* Two CTA cards */}
      <section className="ctas">
        <button type="button" className="cta cta-now" onClick={startFresh}>
          <span className="cta-icon">+</span>
          <span className="cta-body">
            <strong>Start a new room</strong>
            <em>Fresh slug, instant link to share.</em>
          </span>
          <span className="cta-arrow" aria-hidden>→</span>
        </button>
        <button
          type="button"
          className="cta cta-schedule"
          onClick={() => setOpenSchedule(true)}
          disabled={!props.signedIn}
        >
          <span className="cta-icon" aria-hidden>📅</span>
          <span className="cta-body">
            <strong>Schedule a meeting</strong>
            <em>{props.signedIn ? "Picks a room slug, posts to your Google Calendar." : "Sign in to schedule."}</em>
          </span>
          <span className="cta-arrow" aria-hidden>→</span>
        </button>
      </section>

      {/* Quick join */}
      <section className="join">
        <span className="join-label">Or jump into</span>
        <input
          type="text"
          value={joinName}
          onChange={(e) => setJoinName(e.target.value)}
          placeholder="room-name"
          onKeyDown={(e) => { if (e.key === "Enter") goRoom(joinName.trim()); }}
          className="join-input"
        />
        <button
          type="button"
          className="join-btn"
          disabled={!joinName.trim()}
          onClick={() => goRoom(joinName.trim())}
        >
          Join →
        </button>
      </section>

      {/* Recents */}
      <section className="block">
        <CollapsibleHead
          label="RECENT ROOMS"
          collapsed={recentsCollapsed}
          onToggle={toggleRecents}
          right={recents.length > 0 ? (
            <button
              type="button"
              className="block-link"
              onClick={(e) => {
                e.stopPropagation();
                if (!window.confirm("Clear all recent rooms?")) return;
                writeRecents([]);
                setRecents([]);
              }}
            >Clear</button>
          ) : null}
        />
        {!recentsCollapsed ? (<>
        {sortedRecents.length === 0 ? (
          <div className="empty">No rooms yet. Pop one open above.</div>
        ) : (
          <div className="recents-strip">
            {sortedRecents.map((r, idx) => {
              const display = r.displayName?.trim() || r.name;
              const initials = display
                .replace(/[^a-zA-Z0-9]/g, " ")
                .trim()
                .split(/\s+/)
                .map((w) => w[0]?.toUpperCase() ?? "")
                .slice(0, 2)
                .join("") || display.slice(0, 2).toUpperCase();
              const hue = Array.from(r.name).reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 0);
              const imageSrc = r.customImage || r.autoAvatar || null;
              return (
                <div key={r.name} className="recent-chip" style={{ animationDelay: `${idx * 30}ms` }}>
                  <button
                    type="button"
                    className="recent-chip-bubble"
                    onClick={() => goRoom(r.name)}
                    aria-label={`Join ${display}`}
                    style={
                      imageSrc
                        ? { background: "#0c0f1f", backgroundImage: `url(${imageSrc})`, backgroundSize: "cover", backgroundPosition: "center" }
                        : { background: `linear-gradient(135deg, hsl(${hue},65%,55%) 0%, hsl(${(hue + 40) % 360},60%,45%) 100%)` }
                    }
                  >
                    {!imageSrc ? <span className="recent-chip-initials">{initials}</span> : null}
                    {r.fav ? <span className="recent-chip-fav">★</span> : null}
                  </button>
                  <div className="recent-chip-label" title={display}>{display}</div>
                  <div className="recent-chip-time">{relTime(r.lastVisitedMs)}</div>
                  <div className="recent-chip-actions">
                    <button
                      type="button"
                      className={`recent-chip-iconbtn ${r.fav ? "active" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        const next = readRecents().map((x) => (x.name === r.name ? { ...x, fav: !x.fav } : x));
                        writeRecents(next); setRecents(next);
                      }}
                      title={r.fav ? "Unfavorite" : "Favorite"}
                      data-testid={`chip-fav-${r.name}`}
                    >{r.fav ? "★" : "☆"}</button>
                    <button
                      type="button"
                      className="recent-chip-iconbtn"
                      onClick={(e) => { e.stopPropagation(); setEditChip(r); }}
                      title="Edit"
                      data-testid={`chip-edit-${r.name}`}
                    >✎</button>
                    <button
                      type="button"
                      className="recent-chip-iconbtn danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        const next = readRecents().filter((x) => x.name !== r.name);
                        writeRecents(next); setRecents(next);
                      }}
                      title="Remove"
                      data-testid={`chip-remove-${r.name}`}
                    >✕</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>) : null}
      </section>


      {/* Google Calendar agenda */}
      {props.signedIn ? (
        <section className="block">
          <CollapsibleHead
            label="FROM GOOGLE CALENDAR (next 14 days)"
            collapsed={calendarCollapsed}
            onToggle={toggleCalendar}
            right={<a className="block-link" href="https://calendar.google.com/" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Open ↗</a>}
          />
          {!calendarCollapsed ? (<>
          {calendarEvents === null ? (
            calendarReason === "no_token" ? (
              <div className="empty">
                We don&apos;t have Calendar access yet — sign out and back in so Google can grant it.
              </div>
            ) : (
              <div className="loading-row" />
            )
          ) : calendarEvents.length === 0 ? (
            <div className="empty">Nothing in the next 14 days.</div>
          ) : (
            <ul className="rows">
              {calendarEvents.slice(0, 6).map((e, idx) => {
                const isOurs = e.joinUrl?.includes("jobutupaki.duckdns.org/rooms/") ?? false;
                const expanded = expandedCal.has(e.id);
                const toggleRow = () => {
                  setExpandedCal((prev) => {
                    const n = new Set(prev);
                    if (n.has(e.id)) n.delete(e.id); else n.add(e.id);
                    return n;
                  });
                };
                return (
                  <li
                    key={e.id}
                    className={`row sched-row${expanded ? " expanded" : ""}`}
                    style={{ animationDelay: `${idx * 40}ms` }}
                  >
                    <button
                      type="button"
                      className="row-icon-btn"
                      onClick={toggleRow}
                      aria-label={expanded ? "Collapse" : "Expand"}
                    >
                      <span className="row-icon cal" aria-hidden>{isOurs ? "🎥" : "🗓️"}</span>
                    </button>
                    <button
                      type="button"
                      className="row-main row-clickable"
                      onClick={toggleRow}
                    >
                      <span className="row-title">{e.summary}</span>
                      <span className="row-meta">
                        {new Date(e.startMs).toLocaleString()}
                        {e.attendees.length ? ` · ${e.attendees.length} invited` : ""}
                      </span>
                    </button>
                    <span className="row-when">{timeLeft(e.startMs)}</span>
                    {e.joinUrl ? (
                      isOurs ? (
                        <a className="row-action" href={e.joinUrl} onClick={(ev) => ev.stopPropagation()}>Join</a>
                      ) : (
                        <a className="row-action ghost" href={e.joinUrl} target="_blank" rel="noopener noreferrer" onClick={(ev) => ev.stopPropagation()}>Open ↗</a>
                      )
                    ) : (
                      <a className="row-action ghost" href={e.htmlLink} target="_blank" rel="noopener noreferrer" onClick={(ev) => ev.stopPropagation()}>View ↗</a>
                    )}
                    {expanded ? (
                      <div className="row-expanded">
                        {e.description ? (
                          <p className="row-desc">{e.description.slice(0, 800)}{e.description.length > 800 ? "…" : ""}</p>
                        ) : (
                          <p className="row-desc empty-desc">No description on this event.</p>
                        )}
                        {e.location ? (
                          <p className="row-loc">📍 {e.location}</p>
                        ) : null}
                        {e.attendees.length ? (
                          <p className="row-att">Invited: {e.attendees.map((a) => a.email).join(", ")}</p>
                        ) : null}
                        <div className="row-actions">
                          <a className="row-action ghost" href={e.htmlLink} target="_blank" rel="noopener noreferrer" onClick={(ev) => ev.stopPropagation()}>Open in Google Calendar ↗</a>
                        </div>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </>) : null}
        </section>
      ) : null}

      {/* Upcoming */}
      {props.signedIn ? (
        <section className="block">
          <CollapsibleHead
            label="UPCOMING"
            collapsed={upcomingCollapsed}
            onToggle={toggleUpcoming}
            right={<button type="button" className="block-link" onClick={(e) => { e.stopPropagation(); setOpenSchedule(true); }}>Manage</button>}
          />
          {!upcomingCollapsed ? (<>
          {schedules === null ? (
            <div className="loading-row" />
          ) : (dedupedSchedules ?? []).length === 0 ? (
            <div className="empty">No scheduled meetings. Click <strong>Schedule a meeting</strong> above.</div>
          ) : (
            <ul className="rows">
              {(dedupedSchedules ?? []).map((s, idx) => (
                <li
                  key={s.id}
                  className="row sched-row"
                  style={{ animationDelay: `${idx * 50}ms` }}
                >
                  <span className="row-icon sched">📅</span>
                  <span className="row-main">
                    <span className="row-title">{s.title}</span>
                    <span className="row-meta">
                      {new Date(s.startMs).toLocaleString()} · /rooms/{s.roomName}
                      {s.attendees.length ? ` · ${s.attendees.length} invited` : ""}
                    </span>
                  </span>
                  <span className="row-when">{timeLeft(s.startMs)}</span>
                  <button type="button" className="row-action" onClick={() => goRoom(s.roomName)}>Join</button>
                </li>
              ))}
            </ul>
          )}
      </>) : null}
      </section>
      ) : null}

      <ScheduleModalLazy
        open={openSchedule}
        onClose={() => setOpenSchedule(false)}
        onChanged={async () => {
          try {
            const res = await fetch("/api/schedules");
            if (res.ok) {
              const j = (await res.json()) as { schedules: Schedule[] };
              setSchedules(j.schedules.slice(0, 4));
            }
          } catch { /* noop */ }
        }}
      />

      <style>{`
        .dash { display: flex; flex-direction: column; gap: 24px;
          animation: dash-in 460ms cubic-bezier(.2,.8,.2,1); }
        @keyframes dash-in { from { opacity:0; transform: translateY(10px); } to { opacity:1; transform: translateY(0); } }

        /* Hero */
        .hero {
          background: var(--jobu-card);
          border: 1px solid var(--jobu-card-border);
          border-radius: 20px;
          padding: 24px 26px;
          backdrop-filter: blur(20px) saturate(140%);
          -webkit-backdrop-filter: blur(20px) saturate(140%);
          box-shadow: var(--jobu-card-shadow);
        }
        .hero-id { display: flex; align-items: center; gap: 16px; }
        .hero-avatar {
          width: 64px; height: 64px;
          border-radius: 50%;
          border: 2px solid rgba(255,255,255,0.18);
          box-shadow: 0 8px 24px -8px rgba(91,141,239,0.45);
        }
        .hero-avatar.fallback {
          display: grid; place-items: center;
          background: linear-gradient(135deg, #5b8def 0%, #7c5bef 100%);
          color: #fff; font-size: 24px; font-weight: 700;
        }
        .hero-text { display: flex; flex-direction: column; }
        .hero-greet {
          margin: 0; font-size: 28px; font-weight: 700; letter-spacing: -0.4px;
          background: linear-gradient(135deg, var(--jobu-greet-stop1) 0%, var(--jobu-greet-stop2) 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
        .hero-mail { margin: 2px 0 0; font-size: 12px; opacity: 0.55; }
        .hero-sub { margin: 14px 0 0; font-size: 13px; opacity: 0.65; line-height: 1.55; max-width: 56ch; }

        /* CTAs */
        .ctas {
          display: grid; grid-template-columns: 1fr 1fr; gap: 14px;
        }
        @media (max-width: 720px) { .ctas { grid-template-columns: 1fr; } }
        .cta {
          display: grid;
          grid-template-columns: auto 1fr auto;
          align-items: center; gap: 14px;
          padding: 18px 18px;
          border: 0; border-radius: 16px;
          font: inherit; cursor: pointer; text-align: left;
          color: #fff;
          transition: transform 180ms cubic-bezier(.2,.8,.2,1), box-shadow 200ms;
          position: relative;
          overflow: hidden;
        }
        .cta:hover { transform: translateY(-2px); }
        .cta:active { transform: translateY(0); }
        .cta::after {
          content: "";
          position: absolute; inset: 0;
          background: linear-gradient(120deg, transparent 30%, rgba(255,255,255,.18) 50%, transparent 70%);
          transform: translateX(-100%);
          transition: transform 800ms;
          pointer-events: none;
        }
        .cta:hover::after { transform: translateX(100%); }
        .cta-now {
          background: linear-gradient(135deg, #5b8def 0%, #7c5bef 100%);
          box-shadow: 0 14px 30px -12px rgba(91,141,239,0.55);
        }
        .cta-now:hover { box-shadow: 0 18px 38px -10px rgba(91,141,239,0.7); }
        .cta-schedule {
          background: linear-gradient(135deg, #22a06b 0%, #1d8a55 100%);
          box-shadow: 0 14px 30px -12px rgba(34,160,107,0.45);
        }
        .cta-schedule[disabled] {
          background: var(--jobu-row-bg);
          color: var(--jobu-text-faint);
          box-shadow: none;
          cursor: not-allowed;
        }
        .cta-schedule:hover:not([disabled]) { box-shadow: 0 18px 38px -10px rgba(34,160,107,0.6); }
        .cta-icon {
          width: 40px; height: 40px;
          display: grid; place-items: center;
          background: rgba(255,255,255,0.18);
          border-radius: 12px;
          font-size: 20px; font-weight: 700; line-height: 1;
        }
        .cta-body { display: grid; gap: 3px; }
        .cta-body strong { font-size: 15px; font-weight: 600; }
        .cta-body em { font-style: normal; font-size: 12px; opacity: 0.85; line-height: 1.4; }
        .cta-arrow { font-size: 18px; opacity: 0.85; }

        /* Quick join */
        .join {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 12px;
          background: var(--jobu-row-bg);
          border: 1px solid var(--jobu-card-border);
          border-radius: 12px;
        }
        .join-label { font-size: 11px; opacity: 0.55; letter-spacing: 0.6px; text-transform: uppercase; padding-left: 6px; }
        .join-input {
          flex: 1; padding: 8px 12px;
          background: var(--jobu-icon-bg);
          color: var(--jobu-text);
          border: 1px solid var(--jobu-input-border);
          border-radius: 8px;
          font: inherit; font-size: 13px;
          outline: none;
          transition: border-color 150ms, box-shadow 200ms;
        }
        .join-input:focus { border-color: #5b8def; box-shadow: 0 0 0 3px rgba(91,141,239,0.25); }
        .join-btn {
          padding: 7px 14px;
          background: rgba(91,141,239,0.85);
          color: #fff;
          border: 0; border-radius: 8px;
          font: inherit; font-size: 12px; font-weight: 600;
          cursor: pointer;
        }
        .join-btn[disabled] { opacity: 0.4; cursor: not-allowed; }
        .join-btn:hover:not([disabled]) { background: #5b8def; }

        /* Blocks */
        .block {
          background: var(--jobu-card);
          border: 1px solid var(--jobu-row-border);
          border-radius: 16px;
          padding: 14px 16px 18px;
        }
        .block-head {
          display: flex; justify-content: space-between; align-items: baseline;
          padding: 0 4px 8px;
          font-size: 11px; letter-spacing: 1.4px; font-weight: 600; color: var(--jobu-text-dim);
        }
        .block-link {
          background: transparent; border: 0; cursor: pointer;
          color: #7eb8ff; font: inherit; font-size: 11px; letter-spacing: 0;
        }
        .empty {
          padding: 16px 12px; text-align: center;
          font-size: 12px; opacity: 0.55;
        }
        .loading-row {
          height: 56px; border-radius: 10px;
          background: linear-gradient(90deg, rgba(255,255,255,0.04), rgba(255,255,255,0.08), rgba(255,255,255,0.04));
          background-size: 200% 100%;
          animation: shimmer 1.4s linear infinite;
        }
        @keyframes shimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }

        .rows { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 6px; }
        
        .row {
          display: grid;
          grid-template-columns: auto 1fr auto auto auto;
          align-items: center; gap: 10px;
          padding: 10px 12px;
          background: var(--jobu-row-bg);
          border: 1px solid var(--jobu-row-border);
          border-radius: 10px;
          transition: background 120ms, transform 120ms cubic-bezier(.2,.8,.2,1), border-color 120ms;
          animation: row-in 360ms cubic-bezier(.2,.8,.2,1) both;
        }
        @keyframes row-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .row:hover { background: var(--jobu-row-bg-hover); border-color: var(--jobu-row-border-hover); transform: translateX(2px); }
        .sched-row { grid-template-columns: auto 1fr auto auto; }
        .recent-row { grid-template-columns: auto 1fr auto auto; }

        .row-icon, .row-icon-btn {
          width: 36px; height: 36px;
          display: grid; place-items: center;
          border: 0; padding: 0;
          background: var(--jobu-icon-bg);
          color: inherit;
          border-radius: 9px;
          font-size: 16px;
          cursor: pointer;
        }
        .row-icon-btn:hover .row-icon { background: rgba(255,255,255,0.12); }
        .row-icon.fav { background: linear-gradient(135deg, #f7c948 0%, #d99e2c 100%); color: #1a1305; }
        .row-icon.sched { background: linear-gradient(135deg, #22a06b 0%, #1d8a55 100%); color: #fff; }
        .row-icon.cal { background: linear-gradient(135deg, #ff6b6b 0%, #c94b4b 100%); color: #fff; }
        .row-main {
          display: flex; flex-direction: column; gap: 2px; min-width: 0;
        }
        .row-clickable {
          background: transparent; border: 0; padding: 0; text-align: left;
          color: inherit; cursor: pointer;
        }
        .row-title { font-size: 14px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .row-meta { font-size: 11px; opacity: 0.55; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .row-when {
          font-size: 11px; opacity: 0.7; min-width: 60px; text-align: right;
          padding: 3px 8px; border-radius: 999px;
          background: var(--jobu-pill-bg); color: var(--jobu-pill-text);
        }
        .row-action {
          padding: 6px 12px; font: inherit; font-size: 12px; font-weight: 600;
          background: rgba(91,141,239,0.85); color: #fff;
          border: 0; border-radius: 8px; cursor: pointer;
          transition: background 120ms;
        }
        .row-action:hover { background: #5b8def; }
        .row-action.ghost { background: var(--jobu-icon-bg); border: 1px solid var(--jobu-input-border); text-decoration: none; }
        .row-action.ghost:hover { background: rgba(255,255,255,0.12); }
        .sched-row.expanded { grid-template-columns: auto 1fr auto auto; grid-template-rows: auto auto; }
        .sched-row.expanded > .row-expanded {
          grid-column: 1 / -1;
          padding: 8px 4px 2px;
          border-top: 1px solid var(--jobu-divider, rgba(255,255,255,0.08));
          margin-top: 6px;
          display: flex; flex-direction: column; gap: 6px;
          animation: row-expand-in 200ms cubic-bezier(.2,.8,.2,1);
        }
        @keyframes row-expand-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        .row-desc { margin: 0; font-size: 12px; line-height: 1.55; color: var(--jobu-text-soft, rgba(232,236,255,0.72)); white-space: pre-wrap; }
        .row-desc.empty-desc { font-style: italic; opacity: 0.55; }
        .row-loc { margin: 0; font-size: 11px; color: var(--jobu-text-dim); }
        .row-att { margin: 0; font-size: 11px; color: var(--jobu-text-dim); word-break: break-all; }
        .row-actions { display: flex; gap: 6px; margin-top: 4px; }
        .row-fav, .row-x {
          background: transparent; border: 0; padding: 6px 8px;
          font-size: 14px; color: var(--jobu-text-faint);
          border-radius: 6px; cursor: pointer;
          transition: color 120ms, background 120ms;
        }
        .row-fav:hover { color: #f7c948; background: rgba(247,201,72,0.1); }
        .row-fav.active { color: #f7c948; }
        .row-x:hover { color: #ff8a8a; background: rgba(255,138,138,0.1); }
      `}</style>
      <footer style={{
        marginTop: 48,
        padding: "20px 0 32px",
        borderTop: "1px solid rgba(255,255,255,0.06)",
        color: "rgba(232, 236, 255, 0.55)",
        fontSize: 12,
        textAlign: "center",
      }}>
        <span style={{ marginRight: 12 }}>Jobutupaki Meet</span>
        <Link href="/privacy" style={{ color: "rgba(126, 184, 255, 0.85)", textDecoration: "none", marginRight: 12 }}>Privacy</Link>
        <Link href="/terms" style={{ color: "rgba(126, 184, 255, 0.85)", textDecoration: "none", marginRight: 12 }}>Terms</Link>
        <a href="mailto:schwarznir@gmail.com" style={{ color: "rgba(126, 184, 255, 0.85)", textDecoration: "none" }}>Contact</a>
      </footer>
      {editChip ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Edit room"
          onClick={(e) => { if (e.target === e.currentTarget) setEditChip(null); }}
          style={{
            position: "fixed", inset: 0, zIndex: 80,
            background: "rgba(6, 7, 15, 0.55)",
            backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
            display: "grid", placeItems: "center", padding: 16,
          }}
        >
          <div
            style={{
              width: 380, maxWidth: "100%",
              background: "var(--lk-bg2, #14182c)",
              border: "1px solid var(--lk-border-color, rgba(255,255,255,0.1))",
              borderRadius: 14, padding: 18,
              color: "var(--lk-fg, #e8ecff)",
              boxShadow: "0 16px 40px -8px rgba(0,0,0,0.5)",
            }}
          >
            <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 600 }}>Edit room</h3>
            <label style={{ display: "block", fontSize: 11, opacity: 0.7, marginBottom: 4 }}>Display name</label>
            <input
              type="text"
              value={editChip.displayName ?? ""}
              placeholder={editChip.name}
              data-testid="edit-displayname-input"
              onChange={(e) => {
                const v = e.target.value;
                const next = readRecents().map((x) => (x.name === editChip.name ? { ...x, displayName: v.trim() || undefined } : x));
                writeRecents(next); setRecents(next);
                setEditChip((p) => (p ? { ...p, displayName: v.trim() || undefined } : p));
              }}
              style={{
                width: "100%", padding: "8px 10px", borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(0,0,0,0.25)", color: "inherit",
                fontSize: 13,
              }}
            />
            <div style={{ fontSize: 11, opacity: 0.55, marginTop: 4 }}>URL slug stays <code>{editChip.name}</code></div>

            <label style={{ display: "block", fontSize: 11, opacity: 0.7, marginTop: 14, marginBottom: 4 }}>Custom image</label>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {(editChip.customImage || editChip.autoAvatar) ? (
                <img src={editChip.customImage || editChip.autoAvatar} alt="" style={{ width: 48, height: 48, borderRadius: 24, objectFit: "cover" }} />
              ) : (
                <div style={{ width: 48, height: 48, borderRadius: 24, background: "rgba(255,255,255,0.06)", display: "grid", placeItems: "center", fontSize: 11, opacity: 0.55 }}>none</div>
              )}
              <input
                type="file"
                accept="image/*"
                data-testid="edit-image-input"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => {
                    const data = String(reader.result);
                    const next = readRecents().map((x) => (x.name === editChip.name ? { ...x, customImage: data } : x));
                    writeRecents(next); setRecents(next);
                    setEditChip((p) => (p ? { ...p, customImage: data } : p));
                  };
                  reader.readAsDataURL(file);
                }}
                style={{ fontSize: 12, color: "inherit" }}
              />
              {editChip.customImage ? (
                <button
                  type="button"
                  onClick={() => {
                    const next = readRecents().map((x) => (x.name === editChip.name ? { ...x, customImage: undefined } : x));
                    writeRecents(next); setRecents(next);
                    setEditChip((p) => (p ? { ...p, customImage: undefined } : p));
                  }}
                  style={{
                    fontSize: 11, padding: "4px 8px", borderRadius: 6,
                    border: "1px solid rgba(255,255,255,0.18)", background: "transparent",
                    color: "inherit", cursor: "pointer",
                  }}
                >Reset</button>
              ) : null}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
              <button
                type="button"
                onClick={() => setEditChip(null)}
                style={{
                  padding: "8px 14px", fontSize: 12, fontWeight: 600,
                  background: "linear-gradient(135deg, #5b8def 0%, #7c5bef 100%)",
                  color: "#fff", border: 0, borderRadius: 8, cursor: "pointer",
                }}
              >Done</button>
            </div>
          </div>
        </div>
      ) : null}

    </div>
  );
}

// Lazily import the existing ScheduleModal so opening it doesn't bloat the
// initial bundle. ScheduleButton wraps the modal — we re-implement a thin
// open/close shell here so the modal can be invoked by the CTA card.
function ScheduleModalLazy(props: {
  open: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [Mod, setMod] = React.useState<React.ComponentType<{ signedIn: boolean }> | null>(null);

  React.useEffect(() => {
    if (!props.open) return;
    let cancelled = false;
    void (async () => {
      const { ScheduleButton } = await import("@/lib/ScheduleModal");
      if (!cancelled) setMod(() => ScheduleButton);
    })();
    return () => { cancelled = true; };
  }, [props.open]);

  // The ScheduleModal lib exposes ScheduleButton (button + portal). We can't
  // directly trigger its modal from here without a ref. Instead, we render a
  // hidden trigger and click it programmatically when `props.open` flips on.
  const triggerRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!props.open || !Mod || !triggerRef.current) return;
    const btn = triggerRef.current.querySelector("button");
    if (btn) btn.click();
    // Reset open flag immediately — modal manages its own visibility.
    void props.onChanged();
    props.onClose();
  }, [props.open, Mod, props]);

  return Mod ? (
    <div ref={triggerRef} style={{ display: "none" }}>
      <Mod signedIn={true} />
    </div>
  ) : null;
}